
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Github, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import Typed from 'typed.js';

interface AuthFormProps {
  onAuthSuccess?: () => void;
}

export function AuthForm({ onAuthSuccess }: AuthFormProps) {
  const [loading, setLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const el = useRef(null);
  const typed = useRef<Typed | null>(null);

  useEffect(() => {
    // Pequeño retraso para asegurar que el elemento está montado
    const timer = setTimeout(() => {
      const element = el.current;
      if (element && !typed.current) {
        typed.current = new Typed(element, {
          strings: ['Media', 'Experience', 'Results', 'Audience', 'Future'],
          typeSpeed: 50,
          backSpeed: 50,
          backDelay: 2000,
          loop: true,
          showCursor: true,
          cursorChar: '|',
          autoInsertCss: true,
        });

        console.log('Typed.js initialized'); // Debug log
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (typed.current) {
        typed.current.destroy();
        typed.current = null;
      }
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = isLogin
        ? await supabase.auth.signInWithPassword({ email, password })
        : await supabase.auth.signUp({ email, password });

      if (error) throw error;

      if (!isLogin) {
        toast({
          title: "Registro exitoso",
          description: "Por favor verifica tu correo electrónico",
        });
      } else {
        toast({
          title: "¡Bienvenido de nuevo!",
          description: "Has iniciado sesión correctamente",
        });
        setIsOpen(false);
        onAuthSuccess?.();
        navigate('/');
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGithubLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="p-0 border-none bg-transparent max-w-[100vw] h-[100vh] !rounded-none" aria-describedby="auth-description">
        <DialogTitle className="sr-only">Authentication</DialogTitle>
        <DialogDescription id="auth-description" className="sr-only">
          Sign in or create an account to continue
        </DialogDescription>
        <div className="flex h-full w-full">
          {/* Left side - Auth Form */}
          <div className="w-full md:w-[70%] bg-[#0A0D14] h-full p-4 sm:p-6 md:p-8 flex flex-col justify-center">
            <button
              onClick={handleClose}
              className="absolute top-4 left-4 p-2 text-white/60 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="w-full max-w-sm mx-auto space-y-4 sm:space-y-6">
              {/* Logo */}
              <div className="text-left">
                <img 
                  src="https://tv.whatstudio.es/wp-content/uploads/2025/02/logo_white.svg" 
                  alt="Logo" 
                  className="h-16 sm:h-20 mb-4 sm:mb-6"
                />
              </div>

              <div className="space-y-2 text-left">
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
                  {isLogin ? "Iniciar sesión" : "Crear cuenta"}
                </h2>
                <p className="text-xs sm:text-sm text-gray-400">
                  {isLogin ? (
                    <>¿No tienes una cuenta? <button onClick={() => setIsLogin(false)} className="text-white hover:opacity-80">Créala aquí</button></>
                  ) : (
                    <>¿Ya tienes una cuenta? <button onClick={() => setIsLogin(true)} className="text-white hover:opacity-80">Inicia sesión</button></>
                  )}
                </p>
              </div>

              <div className="space-y-4">
                <Button
                  variant="outline"
                  onClick={handleGithubLogin}
                  className="w-full bg-[#1A1B1E] hover:bg-[#1A1B1E]/80 text-white border-[#2E2F33] hover:border-[#2E2F33]/80 h-10 sm:h-11"
                >
                  <Github className="mr-2 h-4 w-4" />
                  Continuar con Github
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-[#2E2F33]" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-[#0A0D14] px-2 text-gray-500">O continúa con</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="space-y-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="bg-[#1A1B1E] border-[#2E2F33] text-white placeholder:text-gray-500 hover:border-[#2E2F33]/80 focus:border-[#2E2F33] h-10 sm:h-11"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-[#1A1B1E] border-[#2E2F33] text-white placeholder:text-gray-500 hover:border-[#2E2F33]/80 focus:border-[#2E2F33] h-10 sm:h-11"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-white text-black hover:bg-white/90 transition-all duration-200 h-10 sm:h-11 mt-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {isLogin ? "Iniciando sesión..." : "Creando cuenta..."}
                    </>
                  ) : (
                    <>{isLogin ? "Iniciar sesión" : "Crear cuenta"}</>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* Right side - Animated Background */}
          <div className="hidden md:block w-[30%] relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#111827] via-[#1F2937] to-[#374151]">
              <div className="absolute inset-0"
                   style={{
                     background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.4) 100%)'
                   }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/30" />
            </div>

            <div className="relative h-full flex items-center p-8">
              <div className="space-y-4 max-w-md">
                <h3 className="text-3xl font-bold text-white/90 text-left">
                  We create, we deliver
                </h3>
                <p className="text-lg text-white/60 text-left">
                  Our commitment and content goes<br />
                  beyond the
                  <span ref={el} className="text-white ml-1"></span>
                </p>
                <p className="text-sm text-white/40 absolute bottom-8 right-8">
                  Powered by WHATS Studio
                </p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
